import styled from "@emotion/styled"
import React, { useMemo } from "react"
import Select, { CSSObjectWithLabel, InputProps, OptionProps, SingleValueProps, StylesConfig, components } from "react-select"
import { Organization } from "../../../hooks/useUserOrganizations"

type OrgSelectOption = { value: string; label: string; hasActiveSubscription: boolean }

type Props = {
  organizations: Organization[]
  value: string | null
  onChange: (organizationId: string | null) => void
}

const CustomOption: React.FC<OptionProps<OrgSelectOption, false>> = props => (
  <components.Option {...props}>
    <OptionContent>
      <span>{props.data.label}</span>
      {props.data.hasActiveSubscription && <C4BBadge>Business Calendar</C4BBadge>}
    </OptionContent>
  </components.Option>
)

const CustomSingleValue: React.FC<SingleValueProps<OrgSelectOption, false>> = props => (
  <components.SingleValue {...props}>
    <OptionContent>
      <span>{props.data.label}</span>
      {props.data.hasActiveSubscription && <C4BBadge>Business Calendar</C4BBadge>}
    </OptionContent>
  </components.SingleValue>
)

const OrganizationSelector: React.FC<Props> = ({ organizations, value, onChange }) => {
  const options: OrgSelectOption[] = useMemo(
    () =>
      organizations.map(org => ({
        value: org.id,
        label: org.name,
        hasActiveSubscription: org.hasActiveSubscription,
      })),
    [organizations]
  )

  const selectedOption = useMemo(() => options.find(opt => opt.value === value) || null, [options, value])

  const styles: StylesConfig<OrgSelectOption, false> = {
    input: (provided: CSSObjectWithLabel, _state: InputProps<OrgSelectOption, false>) => ({
      ...provided,
      border: 0,
      boxShadow: "none",
      padding: 0,
      margin: 0,
    }),
  }

  return (
    <Select
      value={selectedOption}
      classNamePrefix="cl-select"
      onChange={opt => onChange(opt ? opt.value : null)}
      options={options}
      placeholder="Select an organization..."
      isClearable
      styles={styles}
      components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
    />
  )
}

export default OrganizationSelector

const OptionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const C4BBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`
